/**
 * Stepped registry wizard for ``code-connector`` — mirrors the guided flow of
 * ``ToolGenericRestConfigWizard``: transport → repository → credentials → live test → save.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, GitBranch, KeyRound, Loader2, Lock } from 'lucide-react';
import {
  createSecretsRegistrySecret,
  testSecretsRegistrySecret,
  updateSecretsRegistrySecret,
} from '../../services/toolsApi';

const NBSP = '\u00a0';

const PROVIDERS = [
  { id: 'github', label: 'GitHub', hint: 'github.com — use a PAT with repo scope' },
  { id: 'gitlab', label: 'GitLab', hint: 'GitLab.com or self-managed' },
  { id: 'bitbucket', label: 'Bitbucket', hint: 'Bitbucket Cloud / Data Center' },
  { id: 'codecommit', label: 'AWS CodeCommit', hint: 'IAM from the builder role — no token' },
  { id: 'generic-git', label: 'Other HTTPS git', hint: 'Any git smart-HTTPS host' },
];

function providerLabel(pid) {
  return PROVIDERS.find((p) => p.id === pid)?.label || pid;
}

/** @param {'https'|'ssh'} transport */
function providerOptionsForTransport(transport) {
  if (transport === 'ssh') {
    return PROVIDERS.filter((p) => p.id !== 'codecommit');
  }
  return PROVIDERS;
}

function looksHttpsUrl(u) {
  const s = String(u || '').trim().toLowerCase();
  return s.startsWith('https://') || s.startsWith('http://localhost');
}

function looksSshUrl(u) {
  const s = String(u || '').trim();
  return s.startsWith('git@') || s.startsWith('ssh://');
}

function validateRepoUrl(transport, repoUrl) {
  const u = String(repoUrl || '').trim();
  if (!u) return { ok: false, hint: 'Repository URL is required.' };
  if (transport === 'https') {
    if (!looksHttpsUrl(u)) {
      return {
        ok: false,
        hint: 'Use an https:// clone URL (or http://localhost for local mocks).',
      };
    }
    return { ok: true, hint: '' };
  }
  if (!looksSshUrl(u)) {
    return {
      ok: false,
      hint: 'SSH clone URLs look like git@github.com:org/repo.git or ssh://git@host/path.git',
    };
  }
  return { ok: true, hint: '' };
}

function validateBranch(branch) {
  const b = String(branch || '').trim();
  if (!b) return { ok: true };
  if (b.length > 200 || /[\s#]/.test(b)) {
    return { ok: false, hint: 'Use a simple branch name (no spaces or #).' };
  }
  return { ok: true };
}

function validateLabel(label) {
  const s = String(label || '').trim();
  if (!s) return { ok: false, hint: 'Give this connection a short, memorable name.' };
  if (s.length > 120) return { ok: false, hint: 'Use a shorter label (≤ 120 characters).' };
  return { ok: true };
}

export function mapRegistryCodeConnectorSecretToInitial(secret) {
  if (!secret || typeof secret !== 'object') return undefined;
  const cfg = { ...(secret.config || {}) };
  let v = secret.version;
  if (typeof v !== 'number' || !Number.isFinite(v)) {
    const n = v != null ? Number(v) : NaN;
    v = Number.isFinite(n) ? n : undefined;
  }
  return {
    instanceId: 'ti_registry_existing',
    instanceLabel: String(secret.secretName || '').trim(),
    secretId: String(secret.secretId || '').trim(),
    secretName: secret.secretName,
    config: cfg,
    secretRefs: secret.secretRefs && typeof secret.secretRefs === 'object' ? { ...secret.secretRefs } : {},
    registryLogicalVersion: typeof v === 'number' ? v : undefined,
    authType: String(secret.authType || '').trim(),
  };
}

export default function ToolCodeConnectorRegistryWizard({
  tool,
  open,
  isNewEntry,
  initialEntry,
  registryScope,
  tenantId,
  networkId = '__registry_probe__',
  onRegistryCreated,
  onCancel,
}) {
  void tool;
  const [transport, setTransport] = useState(() =>
    inferTransportFromConfig(initialEntry?.config),
  );
  const [provider, setProvider] = useState(() => String(initialEntry?.config?.provider || 'github'));
  const [repoUrl, setRepoUrl] = useState(() => String(initialEntry?.config?.repoUrl || '').trim());
  const [branch, setBranch] = useState(() => String(initialEntry?.config?.BRANCH || '').trim());
  // Delivery policy (mirrors the github MCP "Create a Pull Request" toggle):
  // ON (default) → builder pushes a feature branch + opens a PR; OFF → it
  // commits changes directly to the configured branch (no PR). Absent on an
  // existing connector → treated as ON, preserving today's behavior.
  const [createPr, setCreatePr] = useState(
    () => initialEntry?.config?.createPr !== false,
  );
  const [credential, setCredential] = useState('');
  const [instanceLabel, setInstanceLabel] = useState(() => String(initialEntry?.instanceLabel || '').trim());
  const [reuseStoredCredential, setReuseStoredCredential] = useState(() =>
    Boolean(initialEntry?.secretRefs?.AUTH_CREDENTIAL && !isNewEntry),
  );
  const [submitting, setSubmitting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [error, setError] = useState('');
  const [localHint, setLocalHint] = useState('');
  const [savedSecretId, setSavedSecretId] = useState(() => String(initialEntry?.secretId || '').trim());
  const [registryVersion, setRegistryVersion] = useState(() => initialEntry?.registryLogicalVersion);
  const [stepIndex, setStepIndex] = useState(0);
  /** New rows must pass Test (or Skip) before save — parity with generic-rest registry flow */
  const [probeGate, setProbeGate] = useState(() => !isNewEntry);
  const [lastTestPayload, setLastTestPayload] = useState(null);

  const providerChoices = useMemo(() => providerOptionsForTransport(transport), [transport]);

  const needsCred = useMemo(() => {
    if (provider === 'codecommit') return false;
    return true;
  }, [provider]);

  const cfgPayload = useMemo(() => {
    const o = {
      provider: String(provider || '').trim().toLowerCase(),
      repoUrl: repoUrl.trim(),
      createPr: Boolean(createPr),
    };
    if (branch.trim()) o.BRANCH = branch.trim();
    return o;
  }, [provider, repoUrl, branch, createPr]);

  useEffect(() => {
    if (!open) return;
    setError('');
    setLocalHint('');
    setLastTestPayload(null);
    setTransport(inferTransportFromConfig(initialEntry?.config));
    setProvider(String(initialEntry?.config?.provider || 'github'));
    setRepoUrl(String(initialEntry?.config?.repoUrl || '').trim());
    setBranch(String(initialEntry?.config?.BRANCH || '').trim());
    setCreatePr(initialEntry?.config?.createPr !== false);
    setCredential('');
    setInstanceLabel(String(initialEntry?.instanceLabel || '').trim());
    setReuseStoredCredential(Boolean(initialEntry?.secretRefs?.AUTH_CREDENTIAL && !isNewEntry));
    setSavedSecretId(String(initialEntry?.secretId || '').trim());
    setRegistryVersion(initialEntry?.registryLogicalVersion);
    setStepIndex(0);
    setProbeGate(!isNewEntry);
  }, [
    open,
    isNewEntry,
    initialEntry?.secretId,
    initialEntry?.instanceLabel,
    initialEntry?.config?.provider,
    initialEntry?.config?.repoUrl,
    initialEntry?.config?.BRANCH,
    initialEntry?.config?.createPr,
    initialEntry?.secretRefs,
    initialEntry?.registryLogicalVersion,
  ]);

  useEffect(() => {
    if (provider === 'codecommit' && transport === 'ssh') {
      setProvider('github');
    }
  }, [transport, provider]);

  const steps = useMemo(
    () => [
      { key: 'name', title: 'Name', subtitle: 'How this connection appears in StepFlow' },
      { key: 'transport', title: 'Clone method', subtitle: 'HTTPS (token) or SSH (private key)' },
      { key: 'repo', title: 'Repository', subtitle: 'Host, clone URL, optional branch' },
      { key: 'auth', title: 'Authenticate', subtitle: needsCred ? 'Stored only in encrypted SSM' : 'No token for CodeCommit' },
      { key: 'verify', title: 'Test & save', subtitle: 'Run a live or broker check before finishing' },
    ],
    [needsCred],
  );
  const maxIdx = steps.length - 1;

  const canAdvanceFromStep = useCallback(
    (idx) => {
      const k = steps[idx]?.key;
      if (k === 'name') return validateLabel(instanceLabel).ok;
      if (k === 'transport') return true;
      if (k === 'repo') {
        const ur = validateRepoUrl(transport, repoUrl);
        const br = validateBranch(branch);
        return ur.ok && br.ok && String(provider || '').trim().length > 0;
      }
      if (k === 'auth') {
        if (!needsCred) return true;
        if (reuseStoredCredential) return true;
        return credential.trim().length > 0;
      }
      if (k === 'verify') return isNewEntry ? probeGate : true;
      return true;
    },
    [
      steps,
      instanceLabel,
      transport,
      repoUrl,
      branch,
      provider,
      needsCred,
      reuseStoredCredential,
      credential,
      isNewEntry,
      probeGate,
    ],
  );

  const goNext = () => {
    if (stepIndex >= maxIdx || !canAdvanceFromStep(stepIndex)) return;
    setStepIndex((i) => i + 1);
    setLocalHint('');
  };
  const goBack = () => {
    setStepIndex((i) => Math.max(0, i - 1));
    setLocalHint('');
  };

  const runServerTest = useCallback(
    async (secretIdLike) => {
      const sid = String(secretIdLike || '').trim();
      if (!sid) return false;
      setTesting(true);
      setError('');
      setLastTestPayload(null);
      try {
        const out = await testSecretsRegistrySecret(sid, { networkId });
        setLastTestPayload(out);
        const ok = String(out?.testStatus || '').toUpperCase() === 'TESTED_OK' && out?.ok !== false;
        if (ok) {
          setProbeGate(true);
          return true;
        }
        setProbeGate(false);
        return false;
      } catch (e) {
        setLastTestPayload({ ok: false, message: e?.message || 'Test failed' });
        setProbeGate(false);
        return false;
      } finally {
        setTesting(false);
      }
    },
    [networkId],
  );

  const persistRow = useCallback(
    async (testAfter) => {
      if (!tenantId?.trim()) throw new Error('Tenant scope is missing.');
      const scope = registryScope && typeof registryScope === 'object' ? registryScope : { kind: 'TENANT' };
      const authTypePid = cfgPayload.provider ? cfgPayload.provider : 'connector';
      const plaintextSecrets = {};
      if (needsCred && !reuseStoredCredential && credential.trim()) {
        plaintextSecrets.AUTH_CREDENTIAL = credential.trim();
      }
      const registryEditId = String(initialEntry?.secretId || savedSecretId || '').trim();

      if (registryEditId) {
        const lv = registryVersion ?? initialEntry?.registryLogicalVersion;
        const patch = {
          secretName: instanceLabel.trim(),
          authType: authTypePid,
          config: cfgPayload,
          ...(Object.keys(plaintextSecrets).length ? { plaintextSecrets } : {}),
        };
        const ifMatchOpt =
          typeof lv === 'number' && Number.isFinite(lv) ? { ifMatch: String(Math.trunc(lv)) } : {};
        const data = await updateSecretsRegistrySecret(registryEditId, patch, ifMatchOpt);
        const updated = data?.secret && typeof data.secret === 'object' ? data.secret : null;
        if (!updated) throw new Error('Unexpected response from secrets registry');
        const nv = updated.version;
        if (typeof nv === 'number' && Number.isFinite(nv)) {
          setRegistryVersion(nv);
        }
        setSavedSecretId(String(updated.secretId || registryEditId).trim());
        if (testAfter) await runServerTest(registryEditId);
        return updated;
      }

      const data = await createSecretsRegistrySecret({
        toolName: 'code-connector',
        secretName: instanceLabel.trim(),
        authType: authTypePid,
        config: cfgPayload,
        ...(Object.keys(plaintextSecrets).length ? { plaintextSecrets } : {}),
        scope,
      });
      const created = data?.secret && typeof data.secret === 'object' ? data.secret : null;
      if (!created?.secretId) throw new Error('Unexpected response from secrets registry');
      const newId = String(created.secretId).trim();
      setSavedSecretId(newId);
      const cv = created.version;
      if (typeof cv === 'number' && Number.isFinite(cv)) {
        setRegistryVersion(cv);
      }
      if (testAfter) await runServerTest(newId);
      return created;
    },
    [
      tenantId,
      registryScope,
      cfgPayload,
      needsCred,
      reuseStoredCredential,
      credential,
      initialEntry?.secretId,
      initialEntry?.registryLogicalVersion,
      registryVersion,
      instanceLabel,
      savedSecretId,
      runServerTest,
    ],
  );

  const handleTestOnly = async () => {
    setError('');
    const sid = String(savedSecretId || initialEntry?.secretId || '').trim();
    try {
      if (sid) {
        await runServerTest(sid);
        return;
      }
      const formReady =
        validateLabel(instanceLabel).ok &&
        validateRepoUrl(transport, repoUrl).ok &&
        validateBranch(branch).ok &&
        String(provider || '').trim().length > 0 &&
        (needsCred ? reuseStoredCredential || credential.trim().length > 0 : true);
      if (!formReady) {
        setError('Complete name, repository, and credentials before testing.');
        return;
      }
      await persistRow(true);
    } catch (exc) {
      setError(exc?.message || 'Could not test connection');
    }
  };

  const handleFinalizeOnVerify = async () => {
    setError('');
    if (isNewEntry && !probeGate) {
      setError('Test the connection (or skip) before finishing.');
      return;
    }
    setSubmitting(true);
    try {
      const row = await persistRow(false);
      onRegistryCreated?.(row);
    } catch (exc) {
      setError(exc?.message || 'Failed to save connector');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !tenantId?.trim()) return null;

  const step = steps[stepIndex] || steps[0];
  const repoCheck = validateRepoUrl(transport, repoUrl);
  const branchCheck = validateBranch(branch);
  const labelCheck = validateLabel(instanceLabel);

  return (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cc-registry-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !submitting && !testing) onCancel?.();
      }}
    >
      <div className="flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-xl">
        <div className="shrink-0 border-b border-gray-100 px-5 py-4">
          <h2 id="cc-registry-title" className="text-base font-semibold text-gray-900">
            {isNewEntry ? 'New repository connection' : 'Edit repository connection'}
          </h2>
          <p className="mt-1 text-xs text-gray-600">
            Universal Builder clones this remote inside AWS CodeBuild.{NBSP}
            Guided steps match how we configure API connections — test before you rely on it in production.
          </p>
        </div>

        <div className="shrink-0 border-b border-gray-100 bg-slate-50 px-5 py-2.5">
          <div className="flex flex-wrap gap-1">
            {steps.map((s, idx) => (
              <button
                key={s.key}
                type="button"
                disabled={
                  idx !== stepIndex &&
                  !Array.from({ length: idx }, (_, j) => j).every((j) => canAdvanceFromStep(j))
                }
                onClick={() => setStepIndex(idx)}
                className={`rounded-full px-2.5 py-1 text-[11px] font-medium ring-1 ring-inset transition-colors ${
                  idx === stepIndex
                    ? 'bg-blue-600 text-white ring-blue-700'
                    : 'bg-white text-gray-600 ring-gray-200 hover:bg-gray-50'
                } disabled:opacity-40`}
              >
                {idx + 1}. {s.title}
              </button>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-gray-500">{step.subtitle}</p>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4 text-xs text-gray-800">
          {step.key === 'name' ? (
            <div className="space-y-3">
              <label className="block font-medium text-gray-900">
                Connection label
                <input
                  type="text"
                  value={instanceLabel}
                  onChange={(e) => setInstanceLabel(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 outline-none focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                  placeholder="e.g. Acme · GitHub prod"
                  autoFocus
                />
              </label>
              {!labelCheck.ok ? (
                <p className="text-[11px] text-amber-800">{labelCheck.hint}</p>
              ) : (
                <p className="text-[11px] text-gray-500">
                  Shown in Connections, attach dialogs, and builder logs — pick something your team recognises.
                </p>
              )}
            </div>
          ) : null}

          {step.key === 'transport' ? (
            <div className="grid gap-3 sm:grid-cols-2">
              <button
                type="button"
                onClick={() => setTransport('https')}
                className={`rounded-xl border-2 px-4 py-4 text-left transition-colors ${
                  transport === 'https'
                    ? 'border-blue-500 bg-blue-50/80 shadow-sm'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <Lock className="h-4 w-4 text-blue-600" aria-hidden />
                  HTTPS
                </div>
                <p className="mt-2 text-[11px] leading-relaxed text-gray-600">
                  Recommended.{NBSP}
                  Paste an <code className="rounded bg-white px-1">https://…git</code> clone URL and a PAT or app password.
                  GitHub.com gets a{NBSP}
                  <strong className="font-medium">live API check</strong> on Test.
                </p>
              </button>
              <button
                type="button"
                onClick={() => setTransport('ssh')}
                className={`rounded-xl border-2 px-4 py-4 text-left transition-colors ${
                  transport === 'ssh'
                    ? 'border-blue-500 bg-blue-50/80 shadow-sm'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <div className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <KeyRound className="h-4 w-4 text-slate-700" aria-hidden />
                  SSH
                </div>
                <p className="mt-2 text-[11px] leading-relaxed text-gray-600">
                  Use <code className="rounded bg-white px-1">git@host:org/repo.git</code>.{NBSP}
                  Paste your <strong className="font-medium">private key</strong> (PEM / OpenSSH) — never the public{" "}
                  <code className="rounded bg-white px-1">.pub</code> file.
                </p>
              </button>
            </div>
          ) : null}

          {step.key === 'repo' ? (
            <div className="space-y-4">
              <label className="block font-medium text-gray-900">
                Host / provider
                <select
                  value={provider}
                  onChange={(e) => setProvider(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 outline-none focus:border-blue-400"
                  data-testid="code-connector-provider"
                >
                  {providerChoices.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </label>
              <p className="text-[11px] text-gray-500">
                {PROVIDERS.find((p) => p.id === provider)?.hint}
              </p>

              <label className="block font-medium text-gray-900">
                Clone URL
                <input
                  type="text"
                  value={repoUrl}
                  onChange={(e) => setRepoUrl(e.target.value)}
                  className="mt-1 w-full rounded-lg border border-gray-200 px-3 py-2 font-mono text-[11px] outline-none focus:border-blue-400"
                  placeholder={
                    transport === 'ssh'
                      ? 'git@github.com:your-org/your-repo.git'
                      : 'https://github.com/your-org/your-repo.git'
                  }
                  data-testid="code-connector-repo-url"
                />
              </label>
              {!repoCheck.ok ? (
                <p className="text-[11px] text-amber-800">{repoCheck.hint}</p>
              ) : null}

              <label className="flex items-start gap-2 font-medium text-gray-900">
                <GitBranch className="mt-0.5 h-4 w-4 shrink-0 text-gray-400" aria-hidden />
                <span className="min-w-0 flex-1">
                  Default branch <span className="font-normal text-gray-500">(optional)</span>
                  <input
                    type="text"
                    value={branch}
                    onChange={(e) => setBranch(e.target.value)}
                    className="mt-1 block w-full rounded-lg border border-gray-200 px-3 py-2 font-normal outline-none focus:border-blue-400"
                    placeholder="main"
                    data-testid="code-connector-branch"
                  />
                </span>
              </label>
              {!branchCheck.ok ? (
                <p className="text-[11px] text-amber-800">{branchCheck.hint}</p>
              ) : null}

              {/* Delivery policy — mirrors the github MCP "Create a Pull
                  Request" toggle. ON (default, safe): push a feature branch and
                  open a PR. OFF: commit directly to the configured branch (no
                  PR) for trunk-based / bot workflows. */}
              <label className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50/60 px-3 py-2.5 font-medium text-gray-900">
                <input
                  type="checkbox"
                  checked={createPr}
                  onChange={(e) => setCreatePr(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 rounded border-gray-300 text-blue-600 focus:ring-blue-400"
                  data-testid="code-connector-create-pr"
                />
                <span className="min-w-0 flex-1">
                  Create a Pull Request
                  <span className="mt-0.5 block text-[11px] font-normal leading-snug text-gray-500">
                    {createPr
                      ? 'Changes are pushed to a new branch and opened as a Pull Request (recommended).'
                      : `Changes are committed directly to ${branch.trim() || 'the default branch'} — no Pull Request.`}
                  </span>
                </span>
              </label>
            </div>
          ) : null}

          {step.key === 'auth' ? (
            <div className="space-y-3">
              {!needsCred ? (
                <div className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-3 text-[11px] leading-relaxed text-slate-800">
                  <strong className="font-semibold">CodeCommit</strong> uses IAM credentials from the builder role.{NBSP}
                  Do not store an AWS secret here — only the repository locator belongs in this form.
                </div>
              ) : (
                <>
                  {transport === 'https' ? (
                    <p className="text-[11px] leading-relaxed text-gray-600">
                      Create a fine-grained or classic token with <strong className="text-gray-800">contents: read</strong>{" "}
                      (and <strong className="text-gray-800">metadata</strong> for GitHub). Values are written to encrypted SSM and
                      never shown again.
                    </p>
                  ) : (
                    <p className="text-[11px] leading-relaxed text-gray-600">
                      Paste the <strong className="text-gray-800">entire private key</strong> including{" "}
                      <code className="rounded bg-gray-100 px-1">BEGIN … PRIVATE KEY</code> lines. Keys are stored encrypted; we
                      validate format when you run Test.
                    </p>
                  )}
                  <label className="block font-medium text-gray-900">
                    {transport === 'https' ? 'Personal access token' : 'SSH private key'}
                    {reuseStoredCredential ? (
                      <span className="ml-2 text-[10px] font-normal text-emerald-700">(stored — leave blank to keep)</span>
                    ) : null}
                    <textarea
                      rows={transport === 'ssh' ? 7 : 3}
                      autoComplete="off"
                      value={credential}
                      onChange={(e) => setCredential(e.target.value)}
                      disabled={reuseStoredCredential}
                      className="mt-1 w-full resize-y rounded-lg border border-gray-200 px-3 py-2 font-mono text-[11px] outline-none focus:border-blue-400"
                      placeholder={
                        reuseStoredCredential
                          ? '(unchanged)'
                          : transport === 'https'
                            ? 'ghp_… or GitLab/Bitbucket token'
                            : '-----BEGIN OPENSSH PRIVATE KEY-----\n...'
                      }
                      data-testid="code-connector-token"
                    />
                  </label>
                  {reuseStoredCredential ? (
                    <button
                      type="button"
                      className="text-[11px] font-medium text-blue-700 hover:underline"
                      onClick={() => setReuseStoredCredential(false)}
                    >
                      Rotate credential…
                    </button>
                  ) : null}
                </>
              )}
            </div>
          ) : null}

          {step.key === 'verify' ? (
            <div className="space-y-4">
              <div className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-3 text-[11px] leading-relaxed text-gray-800">
                <p className="font-semibold text-gray-900">Summary</p>
                <ul className="mt-2 list-inside list-disc space-y-0.5 text-gray-700">
                  <li>
                    <span className="text-gray-500">Label · </span>
                    {instanceLabel.trim() || '—'}
                  </li>
                  <li>
                    <span className="text-gray-500">Method · </span>
                    {transport.toUpperCase()}
                  </li>
                  <li>
                    <span className="text-gray-500">Host · </span>
                    {providerLabel(provider)}
                  </li>
                  <li className="break-all font-mono">
                    <span className="text-gray-500">URL · </span>
                    {repoUrl.trim() || '—'}
                  </li>
                  {branch.trim() ? (
                    <li>
                      <span className="text-gray-500">Branch · </span>
                      {branch.trim()}
                    </li>
                  ) : null}
                </ul>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={testing || submitting}
                  onClick={() => handleTestOnly()}
                  className="inline-flex items-center gap-1 rounded-lg bg-slate-800 px-3 py-2 text-[11px] font-semibold text-white hover:bg-slate-900 disabled:opacity-40"
                  data-testid="code-connector-test-only"
                >
                  {testing ? (
                    <>
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> Testing…
                    </>
                  ) : (
                    'Test connection'
                  )}
                </button>
                {isNewEntry ? (
                  <button
                    type="button"
                    disabled={testing || submitting}
                    onClick={() => {
                      setProbeGate(true);
                      setLocalHint(
                        'Skipped live verification — the builder may still fail if the URL or token is wrong.',
                      );
                    }}
                    className="rounded-lg border border-gray-200 px-3 py-2 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Skip test
                  </button>
                ) : null}
              </div>

              {localHint ? (
                <p className="rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-950">
                  {localHint}
                </p>
              ) : null}

              {lastTestPayload ? (
                <div
                  className={`rounded-lg border px-3 py-3 text-[11px] ${
                    String(lastTestPayload.testStatus || '').toUpperCase() === 'TESTED_OK' &&
                    lastTestPayload.ok !== false
                      ? 'border-emerald-200 bg-emerald-50 text-emerald-950'
                      : 'border-rose-200 bg-rose-50 text-rose-950'
                  }`}
                >
                  <p className="font-semibold">
                    {String(lastTestPayload.testStatus || '').toUpperCase() === 'TESTED_OK' &&
                    lastTestPayload.ok !== false
                      ? 'Connection check passed'
                      : 'Connection check failed'}
                  </p>
                  {lastTestPayload.message ? (
                    <p className="mt-1.5 leading-relaxed">{String(lastTestPayload.message)}</p>
                  ) : null}
                  {lastTestPayload.lastTestError ? (
                    <p className="mt-1.5 whitespace-pre-wrap leading-relaxed text-rose-900">
                      {String(lastTestPayload.lastTestError)}
                    </p>
                  ) : null}
                  <details className="mt-2">
                    <summary className="cursor-pointer text-[10px] font-medium uppercase tracking-wide text-gray-600">
                      Full response
                    </summary>
                    <pre className="mt-2 max-h-48 overflow-auto rounded border border-black/5 bg-black/[0.03] p-2 font-mono text-[10px] leading-relaxed text-gray-800">
                      {JSON.stringify(lastTestPayload, null, 2)}
                    </pre>
                  </details>
                </div>
              ) : (
                <p className="text-[11px] text-gray-500">
                  Test runs a broker resolve plus — for GitHub HTTPS — a live GitHub API request, so you see the same calibre of
                  signal as &quot;Test connection&quot; on generic REST APIs.
                </p>
              )}
            </div>
          ) : null}

          {error ? (
            <div className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[11px] text-red-800">{error}</div>
          ) : null}
        </div>

        <div className="flex shrink-0 flex-wrap items-center justify-between gap-2 border-t border-gray-100 px-5 py-3">
          <div className="flex gap-1">
            <button
              type="button"
              disabled={stepIndex === 0 || submitting || testing}
              onClick={goBack}
              className="inline-flex items-center gap-0.5 rounded-lg border border-gray-200 px-2.5 py-1.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >
              <ChevronLeft className="h-3.5 w-3.5" aria-hidden /> Back
            </button>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <button
              type="button"
              disabled={submitting || testing}
              onClick={onCancel}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-[11px] font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            {step.key === 'verify' ? (
              <button
                type="button"
                disabled={submitting || testing || (isNewEntry && !probeGate)}
                onClick={handleFinalizeOnVerify}
                className="rounded-lg bg-blue-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40"
                data-testid="code-connector-save"
              >
                {submitting ? 'Saving…' : 'Save connection'}
              </button>
            ) : (
              <button
                type="button"
                disabled={!canAdvanceFromStep(stepIndex) || submitting || testing}
                onClick={goNext}
                className="inline-flex items-center gap-0.5 rounded-lg bg-blue-600 px-3 py-1.5 text-[11px] font-semibold text-white hover:bg-blue-700 disabled:opacity-40"
              >
                Next <ChevronRight className="h-3.5 w-3.5" aria-hidden />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function inferTransportFromConfig(cfg) {
  const u = String(cfg?.repoUrl || '').trim();
  if (looksSshUrl(u)) return 'ssh';
  return 'https';
}
